import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Search, Globe, X, ExternalLink, Copy, Check, RefreshCw, AlertCircle, CheckCircle, Clock, ChevronDown, ChevronUp, Info, Settings } from 'lucide-react';
import { apiService, DomainDto, DnsConfigDto, DomainVerificationStatus } from '../services/api';
import { useAlert } from '../contexts/AlertContext';
import LoadingSpinner from './LoadingSpinner';
import './DesignSystem.css';

// Domain-page-specific widgets with no shared DesignSystem.css equivalent
// (the domain row layout, the DNS instructions panel, the setup steps list).
const domainStyles = `
.dom-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.dom-item {
  display: flex;
  flex-direction: column;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: var(--radius-lg);
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
  overflow: hidden;
}

.dom-item:hover {
  border-color: var(--border-secondary);
  box-shadow: var(--shadow-sm);
}

.dom-item-main {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
}

.dom-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-primary-light);
  border-radius: var(--radius-md);
  color: var(--color-primary);
  flex-shrink: 0;
}

.dom-details {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.dom-name-row {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
}

.dom-name {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
  word-break: break-all;
}

.dom-id {
  font-size: 0.6875rem;
  color: var(--text-muted);
  font-family: var(--font-family-mono);
}

.dom-verification-reason {
  font-size: 0.6875rem;
  color: var(--text-muted);
  margin-top: 0.125rem;
}

.dom-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
}

.dom-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: var(--radius-sm);
  color: var(--text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.dom-action-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.dom-action-btn.danger:hover {
  background: var(--color-error-light);
  color: var(--color-error);
}

.dom-action-btn.copied {
  color: var(--color-success);
}

.dom-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.dom-action-btn.spinning svg {
  animation: dom-spin 1s linear infinite;
}

@keyframes dom-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* DNS Instructions panel */
.dom-dns-panel {
  border-top: 1px solid var(--border-primary);
  background: var(--bg-secondary);
}

.dom-dns-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.625rem 1.25rem;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  transition: color var(--transition-fast);
}

.dom-dns-toggle:hover {
  color: var(--text-primary);
}

.dom-dns-toggle svg {
  flex-shrink: 0;
}

.dom-dns-content {
  padding: 0 1.25rem 1rem;
}

.dom-dns-section {
  margin-bottom: 1rem;
}

.dom-dns-section:last-child {
  margin-bottom: 0;
}

.dom-dns-section h4 {
  margin: 0 0 0.5rem;
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.dom-dns-record {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.625rem 0.75rem;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: var(--radius-md);
  font-family: var(--font-family-mono);
  font-size: 0.75rem;
}

.dom-dns-record-row {
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
}

.dom-dns-record-label {
  color: var(--text-muted);
  min-width: 50px;
}

.dom-dns-record-value {
  color: var(--text-primary);
  word-break: break-all;
  flex: 1;
}

.dom-dns-record-copy {
  padding: 0.25rem;
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: var(--radius-sm);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-fast);
}

.dom-dns-record-copy:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.dom-dns-record-copy.copied {
  color: var(--color-success);
}

.dom-dns-note {
  font-size: 0.6875rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
}

.dom-dns-note svg {
  flex-shrink: 0;
  margin-top: 1px;
}

/* Modal chrome not covered by the shared .modal system */
.dom-modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: var(--radius-sm);
  color: var(--text-muted);
  cursor: pointer;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.dom-modal-close:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

/* DNS Setup steps (shown inside the Add Domain modal) */
.dom-setup-info {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-primary);
}

.dom-setup-info h3 {
  margin: 0 0 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
}

.dom-setup-steps {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.dom-setup-step {
  display: flex;
  gap: 0.75rem;
}

.dom-setup-step-num {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-primary);
  color: var(--text-inverse);
  border-radius: 50%;
  font-size: 0.6875rem;
  font-weight: 600;
  flex-shrink: 0;
}

.dom-setup-step-content {
  flex: 1;
}

.dom-setup-step-content p {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--text-secondary);
}

.dom-setup-step-content code {
  background: var(--bg-tertiary);
  padding: 0.125rem 0.375rem;
  border-radius: var(--radius-sm);
  font-family: var(--font-family-mono);
  font-size: 0.75rem;
  color: var(--text-primary);
}

/* Custom Pages Modal */
.dom-custom-pages-field {
  margin-bottom: 1rem;
}

.dom-custom-pages-field:last-child {
  margin-bottom: 0;
}

.dom-custom-pages-field label {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.dom-custom-pages-info {
  margin-top: 1rem;
  padding: 0.75rem;
  background: var(--bg-tertiary);
  border-radius: var(--radius-md);
  font-size: 0.75rem;
  color: var(--text-secondary);
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.dom-custom-pages-info svg {
  flex-shrink: 0;
  margin-top: 1px;
  color: var(--text-muted);
}
`;

interface CustomPagesState {
  indexUrl: string;
  notFoundUrl: string;
}

const Domains: React.FC = () => {
  const { showToast, showConfirm } = useAlert();
  const [domains, setDomains] = useState<DomainDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formName, setFormName] = useState('');
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());
  const [verifyingDomains, setVerifyingDomains] = useState<Set<string>>(new Set());
  const [dnsConfig, setDnsConfig] = useState<DnsConfigDto | null>(null);
  const [dnsConfigLoading, setDnsConfigLoading] = useState(false);
  const [copiedRecord, setCopiedRecord] = useState<string | null>(null);

  // Custom pages modal state
  const [showCustomPagesModal, setShowCustomPagesModal] = useState(false);
  const [selectedDomainForCustomPages, setSelectedDomainForCustomPages] = useState<DomainDto | null>(null);
  const [customPagesState, setCustomPagesState] = useState<CustomPagesState>({
    indexUrl: '',
    notFoundUrl: '',
  });
  const [loadingCustomPages, setLoadingCustomPages] = useState(false);
  const [savingCustomPages, setSavingCustomPages] = useState(false);

  useEffect(() => {
    fetchDomains();
  }, []);

  const fetchDomains = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.domains.list({ page: 1, pageSize: 100 });
      setDomains(response.data);
    } catch (err) {
      console.error('Failed to fetch domains:', err);
      setError('Failed to load domains. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDnsConfig = async () => {
    if (dnsConfig || dnsConfigLoading) return;
    setDnsConfigLoading(true);
    try {
      const config = await apiService.domains.getDnsConfig();
      setDnsConfig(config);
    } catch (err) {
      console.error('Failed to fetch DNS config:', err);
      // Use defaults if not available
      setDnsConfig({
        txtRecordName: '_shortas-domain-challenge',
        allowedIpv4: ['203.0.113.10'],
        allowedIpv6: []
      });
    } finally {
      setDnsConfigLoading(false);
    }
  };

  const handleCreate = () => {
    setFormName('');
    setShowModal(true);
  };

  useEffect(() => {
    if (!showModal && !showCustomPagesModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showModal && !saving) {
          setShowModal(false);
          e.preventDefault();
          e.stopImmediatePropagation();
        } else if (showCustomPagesModal && !savingCustomPages) {
          setShowCustomPagesModal(false);
          setSelectedDomainForCustomPages(null);
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showModal, saving, showCustomPagesModal, savingCustomPages]);

  const handleSave = async () => {
    const name = formName.trim().toLowerCase();
    if (!name) return;

    setSaving(true);
    try {
      await apiService.domains.create({ name });
      await fetchDomains();
      setShowModal(false);
      setFormName('');
    } catch (err: any) {
      console.error('Failed to save domain:', err);
      showToast(err.response?.data?.message || 'Failed to create domain.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (domain: DomainDto) => {
    const confirmed = await showConfirm(
      `Delete "${domain.name}"? Routes using this domain will be affected.`,
      'Delete domain',
      { confirmLabel: 'Delete', variant: 'danger' }
    );
    if (!confirmed) return;
    try {
      await apiService.domains.delete(domain.id);
      await fetchDomains();
    } catch (err: any) {
      console.error('Failed to delete domain:', err);
      showToast(err.response?.data?.message || 'Failed to delete domain.', 'error');
    }
  };

  const handleVerify = async (domain: DomainDto) => {
    setVerifyingDomains(prev => new Set(prev).add(domain.id));
    try {
      await apiService.domains.triggerVerification(domain.id);
      await fetchDomains();
    } catch (err: any) {
      console.error('Failed to verify domain:', err);
      showToast(err.response?.data?.message || 'Failed to verify domain.', 'error');
    } finally {
      setVerifyingDomains(prev => {
        const next = new Set(prev);
        next.delete(domain.id);
        return next;
      });
    }
  };

  const handleCopy = (domain: DomainDto) => {
    navigator.clipboard.writeText(domain.name);
    setCopiedId(domain.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const handleCopyRecord = (value: string, key: string) => {
    navigator.clipboard.writeText(value);
    setCopiedRecord(key);
    setTimeout(() => setCopiedRecord(null), 1500);
  };

  const toggleDnsPanel = (domainId: string) => {
    const isExpanding = !expandedDomains.has(domainId);
    setExpandedDomains(prev => {
      const next = new Set(prev);
      if (next.has(domainId)) {
        next.delete(domainId);
      } else {
        next.add(domainId);
      }
      return next;
    });
    if (isExpanding) {
      fetchDnsConfig();
    }
  };

  // Custom pages handlers
  const handleOpenCustomPages = async (domain: DomainDto) => {
    setSelectedDomainForCustomPages(domain);
    setCustomPagesState({ indexUrl: '', notFoundUrl: '' });
    setShowCustomPagesModal(true);
    setLoadingCustomPages(true);

    try {
      const customPages = await apiService.domains.getCustomPages(domain.name);
      setCustomPagesState({
        indexUrl: customPages.customIndexUrl || '',
        notFoundUrl: customPages.customNotFoundUrl || '',
      });
    } catch (err) {
      console.error('Failed to load custom pages:', err);
      showToast('Failed to load custom page settings.', 'error');
    } finally {
      setLoadingCustomPages(false);
    }
  };

  const handleSaveCustomPages = async () => {
    if (!selectedDomainForCustomPages) return;

    setSavingCustomPages(true);

    try {
      await apiService.domains.updateCustomPages(selectedDomainForCustomPages.name, {
        customIndexUrl: customPagesState.indexUrl.trim() || null,
        customNotFoundUrl: customPagesState.notFoundUrl.trim() || null,
      });

      setShowCustomPagesModal(false);
      setSelectedDomainForCustomPages(null);
      showToast('Custom page settings saved successfully.', 'success');
    } catch (err: any) {
      console.error('Failed to save custom pages:', err);
      showToast(err.response?.data?.message || 'Failed to save custom page settings.', 'error');
    } finally {
      setSavingCustomPages(false);
    }
  };

  const isValidUrl = (url: string): boolean => {
    if (!url.trim()) return true; // Empty is valid (means no custom page)
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const getStatusBadge = (status: DomainVerificationStatus) => {
    switch (status) {
      case 'Verified':
        return (
          <span className="badge badge-success">
            <CheckCircle size={10} style={{ marginRight: 4 }} />
            Verified
          </span>
        );
      case 'Failed':
        return (
          <span className="badge badge-error">
            <AlertCircle size={10} style={{ marginRight: 4 }} />
            Failed
          </span>
        );
      case 'Pending':
      default:
        return (
          <span className="badge badge-warning">
            <Clock size={10} style={{ marginRight: 4 }} />
            Pending
          </span>
        );
    }
  };

  const formatVerificationReason = (reason: string): string => {
    const reasonMap: Record<string, string> = {
      'not_checked': 'Verification pending',
      'txt_record_valid': 'TXT record verified',
      'txt_record_missing': 'TXT record not found',
      'txt_record_mismatch': 'TXT record value incorrect',
      'a_record_valid': 'A record verified',
      'a_record_invalid': 'A record points to wrong IP',
      'a_record_missing': 'A record not found',
      'aaaa_record_invalid': 'AAAA record points to wrong IP',
      'dns_timeout': 'DNS lookup timed out',
    };
    return reasonMap[reason] || reason;
  };

  const filteredDomains = domains.filter(d =>
    d.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="container">
        <div className="alert alert-error">
          <h3>Error Loading Domains</h3>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchDomains}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{domainStyles}</style>
      <div className="container">
        {/* Page Header */}
        <div className="page-header">
          <div className="page-title-section">
            <h1 className="page-title">Domains</h1>
            <p className="page-subtitle">Connect and verify custom domains for your short links</p>
          </div>
          <div className="page-actions">
            <button className="btn btn-primary btn-sm" onClick={handleCreate}>
              <Plus size={16} />
              Add Domain
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="toolbar">
          <div className="toolbar-section">
            <div className="search-box" style={{ flex: 1, maxWidth: 360, marginBottom: 0 }}>
              <Search size={15} />
              <input
                type="text"
                placeholder="Search domains..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <span className="text-sm text-muted" style={{ whiteSpace: 'nowrap' }}>
              {filteredDomains.length} domain{filteredDomains.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Domain list */}
        {filteredDomains.length > 0 ? (
          <div className="dom-list">
            {filteredDomains.map((domain) => (
              <div key={domain.id} className="dom-item">
                <div className="dom-item-main">
                  <div className="dom-icon">
                    <Globe size={18} />
                  </div>
                  <div className="dom-details">
                    <div className="dom-name-row">
                      <span className="dom-name">{domain.name}</span>
                      {getStatusBadge(domain.verificationStatus)}
                    </div>
                    <div className="dom-id">{domain.id}</div>
                    {domain.verificationStatus !== 'Verified' && domain.verificationReason && (
                      <div className="dom-verification-reason">
                        {formatVerificationReason(domain.verificationReason)}
                      </div>
                    )}
                  </div>
                  <div className="dom-actions">
                    <button
                      className="dom-action-btn"
                      onClick={() => handleOpenCustomPages(domain)}
                      title="Custom pages (index & 404)"
                    >
                      <Settings size={15} />
                    </button>
                    <button
                      className={`dom-action-btn ${verifyingDomains.has(domain.id) ? 'spinning' : ''}`}
                      onClick={() => handleVerify(domain)}
                      title="Verify domain"
                      disabled={verifyingDomains.has(domain.id)}
                    >
                      <RefreshCw size={15} />
                    </button>
                    <button
                      className={`dom-action-btn ${copiedId === domain.id ? 'copied' : ''}`}
                      onClick={() => handleCopy(domain)}
                      title="Copy domain name"
                    >
                      {copiedId === domain.id ? <Check size={15} /> : <Copy size={15} />}
                    </button>
                    <button
                      className="dom-action-btn"
                      onClick={() => window.open(`https://${domain.name}`, '_blank')}
                      title="Open in browser"
                    >
                      <ExternalLink size={15} />
                    </button>
                    <button
                      className="dom-action-btn danger"
                      onClick={() => handleDelete(domain)}
                      title="Delete domain"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>

                {/* DNS Instructions Panel */}
                <div className="dom-dns-panel">
                  <button
                    className="dom-dns-toggle"
                    onClick={() => toggleDnsPanel(domain.id)}
                  >
                    <Info size={14} />
                    <span>DNS Configuration Instructions</span>
                    {expandedDomains.has(domain.id) ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>

                  {expandedDomains.has(domain.id) && dnsConfigLoading && !dnsConfig && (
                    <div className="dom-dns-content" style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
                      Loading DNS configuration...
                    </div>
                  )}
                  {expandedDomains.has(domain.id) && dnsConfig && (
                    <div className="dom-dns-content">
                      <div className="dom-dns-section">
                        <h4>1. Ownership Verification (TXT Record)</h4>
                        <div className="dom-dns-record">
                          <div className="dom-dns-record-row">
                            <span className="dom-dns-record-label">Name:</span>
                            <span className="dom-dns-record-value">{dnsConfig.txtRecordName}.{domain.name}</span>
                            <button
                              className={`dom-dns-record-copy ${copiedRecord === `txt-name-${domain.id}` ? 'copied' : ''}`}
                              onClick={() => handleCopyRecord(`${dnsConfig.txtRecordName}.${domain.name}`, `txt-name-${domain.id}`)}
                              title="Copy"
                            >
                              {copiedRecord === `txt-name-${domain.id}` ? <Check size={12} /> : <Copy size={12} />}
                            </button>
                          </div>
                          <div className="dom-dns-record-row">
                            <span className="dom-dns-record-label">Type:</span>
                            <span className="dom-dns-record-value">TXT</span>
                          </div>
                          <div className="dom-dns-record-row">
                            <span className="dom-dns-record-label">Value:</span>
                            <span className="dom-dns-record-value">{domain.id}</span>
                            <button
                              className={`dom-dns-record-copy ${copiedRecord === `txt-value-${domain.id}` ? 'copied' : ''}`}
                              onClick={() => handleCopyRecord(domain.id, `txt-value-${domain.id}`)}
                              title="Copy"
                            >
                              {copiedRecord === `txt-value-${domain.id}` ? <Check size={12} /> : <Copy size={12} />}
                            </button>
                          </div>
                        </div>
                        <p className="dom-dns-note">
                          <Info size={12} />
                          This TXT record proves you own the domain.
                        </p>
                      </div>

                      <div className="dom-dns-section">
                        <h4>2. Point Domain to Our Servers (A Record)</h4>
                        <div className="dom-dns-record">
                          <div className="dom-dns-record-row">
                            <span className="dom-dns-record-label">Name:</span>
                            <span className="dom-dns-record-value">{domain.name}</span>
                          </div>
                          <div className="dom-dns-record-row">
                            <span className="dom-dns-record-label">Type:</span>
                            <span className="dom-dns-record-value">A</span>
                          </div>
                          {dnsConfig.allowedIpv4.map((ip, idx) => (
                            <div key={idx} className="dom-dns-record-row">
                              <span className="dom-dns-record-label">Value:</span>
                              <span className="dom-dns-record-value">{ip}</span>
                              <button
                                className={`dom-dns-record-copy ${copiedRecord === `a-${domain.id}-${idx}` ? 'copied' : ''}`}
                                onClick={() => handleCopyRecord(ip, `a-${domain.id}-${idx}`)}
                                title="Copy"
                              >
                                {copiedRecord === `a-${domain.id}-${idx}` ? <Check size={12} /> : <Copy size={12} />}
                              </button>
                            </div>
                          ))}
                        </div>
                        <p className="dom-dns-note">
                          <Info size={12} />
                          This A record routes traffic to our servers.
                        </p>
                      </div>

                      {dnsConfig.allowedIpv6.length > 0 && (
                        <div className="dom-dns-section">
                          <h4>3. IPv6 Support (AAAA Record - Optional)</h4>
                          <div className="dom-dns-record">
                            <div className="dom-dns-record-row">
                              <span className="dom-dns-record-label">Name:</span>
                              <span className="dom-dns-record-value">{domain.name}</span>
                            </div>
                            <div className="dom-dns-record-row">
                              <span className="dom-dns-record-label">Type:</span>
                              <span className="dom-dns-record-value">AAAA</span>
                            </div>
                            {dnsConfig.allowedIpv6.map((ip, idx) => (
                              <div key={idx} className="dom-dns-record-row">
                                <span className="dom-dns-record-label">Value:</span>
                                <span className="dom-dns-record-value">{ip}</span>
                                <button
                                  className={`dom-dns-record-copy ${copiedRecord === `aaaa-${domain.id}-${idx}` ? 'copied' : ''}`}
                                  onClick={() => handleCopyRecord(ip, `aaaa-${domain.id}-${idx}`)}
                                  title="Copy"
                                >
                                  {copiedRecord === `aaaa-${domain.id}-${idx}` ? <Check size={12} /> : <Copy size={12} />}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">
              <Globe size={24} />
            </div>
            <div className="empty-state-title">{searchTerm ? 'No matches' : 'No domains yet'}</div>
            <div className="empty-state-description">
              {searchTerm
                ? 'Try a different search term.'
                : 'Add your first custom domain to start creating short links.'}
            </div>
            {!searchTerm && (
              <button className="btn btn-primary btn-sm" style={{ marginTop: '1.25rem' }} onClick={handleCreate}>
                <Plus size={16} />
                Add Domain
              </button>
            )}
          </div>
        )}

        {/* Create modal */}
        {showModal && (
          <div className="modal-overlay" onClick={() => !saving && setShowModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Add Domain</h3>
                <button className="dom-modal-close" onClick={() => !saving && setShowModal(false)}>
                  <X size={16} />
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Domain name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value.toLowerCase())}
                    onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                    placeholder="links.example.com"
                    autoFocus
                    disabled={saving}
                  />
                  <span className="text-xs text-muted">Enter the fully qualified domain name. It will be lowercased automatically.</span>
                </div>

                {/* DNS Setup Instructions */}
                <div className="dom-setup-info">
                  <h3>After adding your domain</h3>
                  <div className="dom-setup-steps">
                    <div className="dom-setup-step">
                      <span className="dom-setup-step-num">1</span>
                      <div className="dom-setup-step-content">
                        <p>Add a <code>TXT</code> record with your domain ID to verify ownership.</p>
                      </div>
                    </div>
                    <div className="dom-setup-step">
                      <span className="dom-setup-step-num">2</span>
                      <div className="dom-setup-step-content">
                        <p>Add an <code>A</code> record pointing to our servers.</p>
                      </div>
                    </div>
                    <div className="dom-setup-step">
                      <span className="dom-setup-step-num">3</span>
                      <div className="dom-setup-step-content">
                        <p>Click the refresh button to verify your DNS configuration.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline btn-sm" onClick={() => setShowModal(false)} disabled={saving}>
                  Cancel
                </button>
                <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !formName.trim()}>
                  {saving ? 'Adding...' : 'Add Domain'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Custom Pages Modal */}
        {showCustomPagesModal && selectedDomainForCustomPages && (
          <div className="modal-overlay" onClick={() => { if (!savingCustomPages) { setShowCustomPagesModal(false); setSelectedDomainForCustomPages(null); } }}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Custom Pages for {selectedDomainForCustomPages.name}</h3>
                <button
                  className="dom-modal-close"
                  onClick={() => { if (!savingCustomPages) { setShowCustomPagesModal(false); setSelectedDomainForCustomPages(null); } }}
                >
                  <X size={16} />
                </button>
              </div>
              <div className="modal-body">
                {loadingCustomPages ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                    Loading custom page settings...
                  </div>
                ) : (
                  <>
                    <div className="dom-custom-pages-field form-group">
                      <label className="form-label">
                        Custom Index Page
                        <span className="text-xs text-muted">(optional)</span>
                      </label>
                      <input
                        type="url"
                        className="form-control"
                        value={customPagesState.indexUrl}
                        onChange={(e) => setCustomPagesState(prev => ({ ...prev, indexUrl: e.target.value }))}
                        placeholder="https://example.com/welcome"
                        disabled={savingCustomPages}
                      />
                      <span className="text-xs text-muted">
                        Users visiting the root of this domain will be redirected to this URL.
                      </span>
                      {customPagesState.indexUrl && !isValidUrl(customPagesState.indexUrl) && (
                        <span className="text-xs status-error" style={{ display: 'block' }}>
                          Please enter a valid URL.
                        </span>
                      )}
                    </div>

                    <div className="dom-custom-pages-field form-group">
                      <label className="form-label">
                        Custom 404 Page
                        <span className="text-xs text-muted">(optional)</span>
                      </label>
                      <input
                        type="url"
                        className="form-control"
                        value={customPagesState.notFoundUrl}
                        onChange={(e) => setCustomPagesState(prev => ({ ...prev, notFoundUrl: e.target.value }))}
                        placeholder="https://example.com/not-found"
                        disabled={savingCustomPages}
                      />
                      <span className="text-xs text-muted">
                        Users visiting non-existent paths on this domain will be redirected to this URL.
                      </span>
                      {customPagesState.notFoundUrl && !isValidUrl(customPagesState.notFoundUrl) && (
                        <span className="text-xs status-error" style={{ display: 'block' }}>
                          Please enter a valid URL.
                        </span>
                      )}
                    </div>

                    <div className="dom-custom-pages-info">
                      <Info size={14} />
                      <span>
                        Leave fields empty to use the default pages. Custom pages are implemented as redirect routes.
                      </span>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => { setShowCustomPagesModal(false); setSelectedDomainForCustomPages(null); }}
                  disabled={savingCustomPages}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleSaveCustomPages}
                  disabled={
                    savingCustomPages ||
                    loadingCustomPages ||
                    Boolean(customPagesState.indexUrl && !isValidUrl(customPagesState.indexUrl)) ||
                    Boolean(customPagesState.notFoundUrl && !isValidUrl(customPagesState.notFoundUrl))
                  }
                >
                  {savingCustomPages ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Domains;
